import { useState } from "react";
import { ImagePlus, Star, Trash2 } from "lucide-react";
import { mediaApi } from "../../../lib/mediaApi";

/* Step 3 — Media. Cover image (single) + gallery (multi). Files upload to
   the server IMMEDIATELY on pick via mediaApi.uploadImage and we store the
   returned URL on the draft. On save, PropertyWizard attaches these URLs as
   PropertyImage rows (cover -> thumbnail + category:"cover", gallery ->
   category:"gallery"). The customer site reads thumbnail + images[]. */
/* Mirrors MAX_BYTES_PDF in the API's media.service.ts. Checked here so a
   60MB file is rejected instantly instead of uploading in full and coming
   back as a generic server error. Keep the two in sync. */
const MAX_PDF_BYTES = 50 * 1024 * 1024;

export function MediaStep({ data, setData }) {
  const setCover = (next) => setData({ ...data, coverImage: next });
  const setGallery = (next) => setData({ ...data, gallery: next });
  const [busy, setBusy] = useState(false);

  async function uploadOne(file) {
    // Cover and gallery images are public marketing assets - watermark them.
    const res = await mediaApi.uploadImage(file, { watermark: true });
    if (!res?.url) throw new Error("Upload returned no URL");
    return { url: res.url, name: file.name };
  }

  // PDF documents (brochure, floor-plan). Upload immediately on pick; store
  // the returned URL string on the draft under the given key.
  async function handlePdf(file, key) {
    if (!file) return;
    if (file.type !== "application/pdf") { alert("Please upload a PDF file."); return; }
    if (file.size > MAX_PDF_BYTES) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      alert(`PDF too large (${mb} MB). Maximum is 50 MB.`);
      return;
    }
    setBusy(true);
    try {
      const res = await mediaApi.uploadImage(file);
      if (!res?.url) throw new Error("Upload returned no URL");
      setData({ ...data, [key]: res.url });
    } catch (err) {
      alert(err?.message || "PDF upload failed");
    } finally { setBusy(false); }
  }

  async function handleCover(file) {
    if (!file) return;
    setBusy(true);
    try { setCover(await uploadOne(file)); }
    catch (err) { alert(err?.message || "Cover upload failed"); }
    finally { setBusy(false); }
  }

  async function handleGallery(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) uploaded.push(await uploadOne(f));
      setGallery([...(data.gallery || []), ...uploaded]);
    } catch (err) {
      alert(err?.message || "Gallery upload failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      {/* Cover */}
      <div>
        <h4 className="text-sm font-semibold text-slate-900 mb-1">Cover image</h4>
        <p className="text-xs text-slate-500 mb-3">Hero image on the listing card and the first slide on the property page.</p>
        {data.coverImage?.url ? (
          <div className="relative inline-block">
            <img src={data.coverImage.url} alt="Cover" className="rounded-xl max-h-48 object-cover ring-1 ring-slate-200" />
            <button onClick={() => setCover(null)} className="absolute top-2 right-2 bg-white/90 hover:bg-white text-slate-700 rounded-md p-1.5 shadow-sm" title="Remove cover">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 rounded-lg ring-1 ring-dashed ring-slate-300 px-4 py-2.5 hover:bg-slate-50 cursor-pointer">
            <ImagePlus className="w-4 h-4 text-slate-400" />
            {busy ? "Uploading…" : "Upload cover image"}
            <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => handleCover(e.target.files?.[0])} />
          </label>
        )}
      </div>

      {/* Gallery */}
      <div>
        <div className="flex items-start justify-between mb-1">
          <h4 className="text-sm font-semibold text-slate-900">Gallery</h4>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 rounded-lg ring-1 ring-dashed ring-slate-300 px-3 py-1.5 hover:bg-slate-50 cursor-pointer">
            <ImagePlus className="w-3.5 h-3.5 text-slate-400" />
            {busy ? "Uploading…" : "Add images"}
            <input type="file" accept="image/*" multiple className="hidden" disabled={busy} onChange={(e) => handleGallery(e.target.files)} />
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-3">Photos shown in the property page slider. Click the star to promote an image to cover.</p>
        {data.gallery && data.gallery.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.gallery.map((g, idx) => (
              <div key={idx} className="relative group">
                <img src={g.url} alt={g.name || ""} className="w-full h-32 object-cover rounded-lg ring-1 ring-slate-200" />
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-colors rounded-lg flex items-end justify-end p-1.5 opacity-0 group-hover:opacity-100">
                  {/* Swap, not move. This used to set the new cover and drop
                      the clicked image from the gallery while discarding the
                      OUTGOING cover entirely — so every star click silently
                      lost an image. Now the old cover takes the clicked one's
                      place and the total stays constant. */}
                  <button onClick={() => {
                    const outgoing = data.coverImage;
                    setData({
                      ...data,
                      coverImage: g,
                      gallery: outgoing?.url
                        ? data.gallery.map((item, i) => (i === idx ? outgoing : item))
                        : data.gallery.filter((_, i) => i !== idx),
                    });
                  }}
                    className="bg-white/90 hover:bg-white text-amber-600 rounded-md p-1.5 mr-1" title="Promote to cover">
                    <Star className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setGallery(data.gallery.filter((_, i) => i !== idx))}
                    className="bg-white/90 hover:bg-white text-red-600 rounded-md p-1.5" title="Remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-slate-400 border border-dashed border-slate-300 rounded-xl flex flex-col items-center gap-2">
            <ImagePlus className="w-6 h-6 text-slate-300" />No gallery images yet
          </div>
        )}
      </div>

      {/* Documents — optional PDFs (brochure + detailed floor plan). When a
          PDF is present the customer site shows its download button; when
          absent the button is hidden. */}
      <div className="border-t border-slate-200 pt-6">
        <h4 className="text-sm font-semibold text-slate-900 mb-1">Documents</h4>
        <p className="text-xs text-slate-500 mb-3">Optional PDFs. The brochure powers the &ldquo;Download brochure&rdquo; button; the floor-plan PDF powers &ldquo;Request detailed PDF&rdquo;. Leave blank to hide those buttons.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1.5">Brochure PDF</div>
            {data.brochureUrl ? (
              <div className="flex items-center justify-between gap-2 rounded-lg ring-1 ring-slate-200 bg-white px-3 py-2">
                <a href={data.brochureUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 truncate hover:underline">View brochure</a>
                <button onClick={() => setData({ ...data, brochureUrl: "" })} className="text-slate-400 hover:text-red-600 shrink-0" title="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 rounded-lg ring-1 ring-dashed ring-slate-300 px-4 py-2.5 hover:bg-slate-50 cursor-pointer w-full">
                <ImagePlus className="w-4 h-4 text-slate-400" />
                {busy ? "Uploading…" : "Upload brochure PDF"}
                <input type="file" accept="application/pdf" className="hidden" disabled={busy} onChange={(e) => handlePdf(e.target.files?.[0], "brochureUrl")} />
              </label>
            )}
          </div>
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1.5">Detailed floor-plan PDF</div>
            {data.floorPlanPdfUrl ? (
              <div className="flex items-center justify-between gap-2 rounded-lg ring-1 ring-slate-200 bg-white px-3 py-2">
                <a href={data.floorPlanPdfUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 truncate hover:underline">View floor-plan PDF</a>
                <button onClick={() => setData({ ...data, floorPlanPdfUrl: "" })} className="text-slate-400 hover:text-red-600 shrink-0" title="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 rounded-lg ring-1 ring-dashed ring-slate-300 px-4 py-2.5 hover:bg-slate-50 cursor-pointer w-full">
                <ImagePlus className="w-4 h-4 text-slate-400" />
                {busy ? "Uploading…" : "Upload floor-plan PDF"}
                <input type="file" accept="application/pdf" className="hidden" disabled={busy} onChange={(e) => handlePdf(e.target.files?.[0], "floorPlanPdfUrl")} />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Walkthrough video — one link per property, shown as a button beside
          "Download Brochure" on the customer site. */}
      <div className="pt-5 border-t border-slate-200">
        <div className="text-sm font-semibold text-slate-900 mb-1">Walkthrough video</div>
        <p className="text-xs text-slate-500 mb-3">Optional. A YouTube (or any video) link. When set, a &ldquo;Watch Walkthrough&rdquo; button appears next to Download Brochure on the property page.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Video link</label>
            <input
              type="url"
              value={data.walkthroughUrl || ""}
              onChange={(e) => setData({ ...data, walkthroughUrl: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=…"
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Button text</label>
            <input
              type="text"
              value={data.walkthroughLabel || ""}
              onChange={(e) => setData({ ...data, walkthroughLabel: e.target.value })}
              placeholder="Watch Walkthrough"
              maxLength={40}
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
